import { useForm } from "react-hook-form";
import { useActionSignatureContext } from "../../actionSignature/providers/ActionSignatureProvider";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { SubmitShiftHandoverRequest } from "../../../services/api/types/shiftHandover/requests/SubmitShiftHandoverRequest";
import {
  useCreateShiftHandoversMutation,
  useDeleteShiftHandoverMutation,
  useSubmitShiftHandoversMutation,
} from "app/services/api/shiftHandoverApi";
import { getFullName } from "ui/utils/nameUtil";
import { useResidentContext } from "app/features/residents/provider/ResidentProvider";

interface Props {
  onSuccess?: () => void;
  residentId: number;
  titleValue: string;
}

export function useHandleSubmitShiftHandover({
  onSuccess,
  residentId,
  titleValue,
}: Props) {
  const { resident } = useResidentContext();
  const { showActionSignatureModal, showSignOffSignatureModal } =
    useActionSignatureContext();
  const { showNewToast, showErrorToast } = useDisplayToast();
  const [submitShiftHandovers] = useSubmitShiftHandoversMutation();
  const [createShiftHandovers] = useCreateShiftHandoversMutation();
  const [deleteShiftHandover] = useDeleteShiftHandoverMutation();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<SubmitShiftHandoverRequest>({
    defaultValues: {
      body: {
        next_user_email: "",
        next_user_password: "",
      },
    },
  });

  const submit = handleSubmit(async () => {
    try {
      const createHandoverResponse = await createShiftHandovers({
        title: titleValue,
        resident_id: residentId,
      }).unwrap();

      let firstAuth, secondAuth;
      try {
        firstAuth = await showActionSignatureModal({
          title: "Confirm Shift Handover",
          description: "Enter your credentials to confirm.",
        });

        secondAuth = await showSignOffSignatureModal({
          title: "Shift Handover Signoff",
          description: `Please input the login credentials for nurse/carer signing off on the handover for ${getFullName(resident)} (Resident ID: ${residentId})`,
          editableEmail: true,
        });
      } catch {
        if (createHandoverResponse.id) {
          await deleteShiftHandover({
            shiftHandoverId: createHandoverResponse.id,
          }).unwrap();
        }
        return;
      }

      const transformedData: SubmitShiftHandoverRequest = {
        body: {
          next_user_email: secondAuth.email,
          next_user_password: secondAuth.password,
          password: firstAuth.password,
        },
        params: {
          shiftHandoverId: createHandoverResponse.id,
        },
      };
      await submitShiftHandovers(transformedData).unwrap();
      if (onSuccess) onSuccess();

      showNewToast({
        title: "Shift handover logged successfully",
        message: "The shift handover has been created and logged successfully",
        type: "success",
      });
    } catch (e) {
      showErrorToast(e);
    }
  });

  return {
    control,
    isSubmitting,
    submit,
  };
}
